import { ContributingFactor } from "../components/ContributingFactorsStep";

/**
 * Gets the count of selected factors
 */
export function getSelectedCount(factors: ContributingFactor[]): number {
	return factors.filter((f) => f.selected).length;
}

/**
 * Gets all selected factors
 */
export function getSelectedFactors(
	factors: ContributingFactor[],
): ContributingFactor[] {
	return factors.filter((f) => f.selected);
}

/**
 * Validates that at least one factor is selected
 */
export function validateFactorSelection(factors: ContributingFactor[]): {
	valid: boolean;
	error?: string;
} {
	const selectedCount = getSelectedCount(factors);

	if (selectedCount === 0) {
		return {
			valid: false,
			error: "Please select at least one contributing factor",
		};
	}

	if (selectedCount > 3) {
		return {
			valid: false,
			error: "You can select a maximum of 3 contributing factors",
		};
	}

	return { valid: true };
}

/**
 * Checks if a factor can be selected (respects max count)
 */
export function canSelectFactor(
	factors: ContributingFactor[],
	factorId: string,
	maxSelectable: number = 3,
): boolean {
	const factor = factors.find((f) => f.id === factorId);
	if (!factor) return false;

	// Can always deselect
	if (factor.selected) return true;

	// Can select if under max count
	const selectedCount = getSelectedCount(factors);
	return selectedCount < maxSelectable;
}

/**
 * Generates a unique ID for a new factor
 */
export function generateFactorId(): string {
	return `factor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Creates a new factor with default values
 */
export function createNewFactor(
	title: string = "New Factor",
	description: string = "",
): ContributingFactor {
	return {
		id: generateFactorId(),
		title,
		description,
		selected: false,
		isEditing: false,
	};
}

/**
 * Toggles the selected state of a factor
 */
export function toggleFactor(
	factors: ContributingFactor[],
	factorId: string,
	maxSelectable: number = 3,
): ContributingFactor[] {
	return factors.map((factor) => {
		if (factor.id === factorId) {
			// Can always deselect
			if (factor.selected) {
				return { ...factor, selected: false };
			}

			// Can select only if under max count
			const selectedCount = getSelectedCount(factors);
			if (selectedCount < maxSelectable) {
				return { ...factor, selected: true };
			}

			// Can't select - already at max
			return factor;
		}
		return factor;
	});
}

/**
 * Updates a factor field
 */
export function updateFactor(
	factors: ContributingFactor[],
	factorId: string,
	field: string,
	value: any,
): ContributingFactor[] {
	return factors.map((factor) => {
		if (factor.id === factorId) {
			return { ...factor, [field]: value };
		}
		return factor;
	});
}

/**
 * Adds a new factor to the list
 */
export function addFactor(
	factors: ContributingFactor[],
	newFactor: ContributingFactor,
): ContributingFactor[] {
	return [...factors, newFactor];
}

/**
 * Removes a factor from the list
 */
export function removeFactor(
	factors: ContributingFactor[],
	factorId: string,
): ContributingFactor[] {
	return factors.filter((f) => f.id !== factorId);
}

/**
 * Sanitizes factor text (trim, normalize whitespace)
 */
export function sanitizeFactorText(text: string): string {
	return text
		.trim()
		.replace(/\s+/g, " ")
		.replace(/\n\s*\n/g, "\n");
}
